// =============================================================================
// voiceCloneErrors — shared parsing + messaging for Voice-Clone generation
// failures across all AI modes (Normal / Inbox / Bulk) and the live preview.
//
// The worldEmailCreator endpoint returns, on a Voice-Clone failure:
//   { error: 'Voice-Clone generation failed', code: '<MACHINE_CODE>|null', details: '<message>' }
//
// `code` is the stable, machine-readable discriminator (e.g. NO_ACTIVE_VARIANT)
// the backend attaches for user-fixable misconfigurations. We key the
// user-facing copy off `code` first and fall back to the raw `details`/status
// so a brand-new backend code still surfaces *something* meaningful.
//
// Generation-time alerts in this app are German (see the existing
// alert()/credit messages), so the user-facing strings here are German and
// lead with a loud "ACHTUNG:" for the actionable misconfiguration case.
// =============================================================================

export const VOICE_CLONE_ERROR_CODES = {
  NO_ACTIVE_VARIANT: 'NO_ACTIVE_VARIANT',
};

// Parse a failed fetch Response from worldEmailCreator into a normalized shape.
// Never throws — a malformed/empty body just yields empty fields.
export const readVoiceCloneError = async (response) => {
  const result = { code: null, details: '', status: response?.status ?? 0 };
  try {
    const body = await response.json();
    result.code = body?.code || null;
    result.details = body?.details || body?.error || '';
  } catch (_) {
    // non-JSON body (e.g. proxy/HTML error page) — keep status only
  }
  return result;
};

// Map a normalized error ({ code, details, status }) to a clear, actionable
// German message for the generation-time alert(). Returns a single string.
export const voiceCloneErrorMessage = ({ code, details, status } = {}) => {
  if (code === VOICE_CLONE_ERROR_CODES.NO_ACTIVE_VARIANT) {
    return (
      'ACHTUNG: Dieses CopyTwin-Template hat keine aktive Struktur-Variante. ' +
      'Die E-Mail kann erst generiert werden, wenn du im Template-Editor unter ' +
      '"Structure variants" mindestens eine Variante hinzufügst und aktivierst.'
    );
  }
  // Generic fallback — still surfaces the backend detail when we have one so
  // support/debugging isn't blind, but stays user-readable.
  const suffix = details ? ` (${details})` : status ? ` (Status ${status})` : '';
  return `Die E-Mail konnte nicht generiert werden.${suffix} Bitte versuche es erneut.`;
};

// Convenience: build an Error carrying the parsed voice-clone error so a
// throw inside a try/await can be inspected in the surrounding catch.
export const buildVoiceCloneError = (parsed) => {
  const err = new Error(voiceCloneErrorMessage(parsed));
  err.vcCode = parsed?.code || null;
  err.vcDetails = parsed?.details || '';
  err.vcStatus = parsed?.status || 0;
  err.vcUserMessage = err.message;
  return err;
};
